import React, { useRef, useState, useEffect } from 'react';
import { Tooltip } from '../tooltips';

type OverflowGuardOptions = {
  hideChildrenFromScreenReader?: boolean;
  showEllipsis?: boolean;
};

type OverflowGuardProps = {
  children: React.ReactNode;
  overflowText: string;
  className?: string;
  options?: OverflowGuardOptions;
};

const OverflowGuard: React.FC<OverflowGuardProps> = ({ children, overflowText, className, options = {} }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    const el = containerRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);

    return () => observer.disconnect();
  }, [children, overflowText]);

  const { hideChildrenFromScreenReader = true, showEllipsis = true } = options;

  return (
    <div className="overflow-guard-wrapper">
      <div
        ref={containerRef}
        className={`overflow-guard ${className || ''}`}
        aria-label={isTruncated && hideChildrenFromScreenReader ? `${overflowText}` : undefined}
        role={isTruncated && hideChildrenFromScreenReader ? 'text' : undefined}
      >
        {isTruncated ? (
          <Tooltip className="info-tooltip tooltip-icon custom-tooltip-v2" message={overflowText}>
            <div aria-hidden={hideChildrenFromScreenReader || undefined}>{children}</div>
          </Tooltip>
        ) : (
          children
        )}
      </div>
      {showEllipsis && isTruncated && (
        <span className="overflow-guard-ellipsis" aria-hidden="true">
          ...
        </span>
      )}
    </div>
  );
};

export default OverflowGuard;
